import React, { useState, ChangeEvent } from "react";
import { useHistory } from "react-router-dom";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import Toggle from "components/form/toggle/Toggle";
import { Select } from "components/select/index";

import { showError, showSuccess } from "redux/actions/ui";

import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import FormLayout from "components/form/FormLayout";
import { PostDiscount } from "api/rpc/2024-04/facilityAdmin/discount/discount";
import { IProduct } from "redux/reducers/models/product";
import { IDepartments } from "api/rpc/2024-04/facility";
import { TCustomerType } from "redux/reducers/models/facility";
import DiscountDateSelect, { ITimeState } from "./DiscountDateSelect/DiscountDateSelect";
import { IMultipleActionDropdownPageAction } from "components/page/PageActions";
import { formatDate } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";
import DiscountCustomerTypeSelect from "./DiscountCustomerTypeSelect/DiscountCustomerTypeSelect";
import DiscountDepartmentSelect from "./DiscountDepartmentSelect/DiscountDepartmentSelect";
import DiscountProductSelect from "./DiscountProductSelect/DiscountProductSelect";
import Sheet from "components/sheet/Sheet";
interface IDiscountState {
  title: string;
  application: any;
  value_type: any;
  value: number;
  custom: boolean;
  tracked: boolean;
  departments?: IDepartments[];
  products?: IProduct[];
  discounted_customer_types?: TCustomerType[];
  apply_discount_ranges?: boolean;
  apply_applicable_times?: boolean;
}

export default function NewDiscount() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const { t } = useTranslation();
  const today = new Date();

  const [discountProductModalVisibility, setDiscountProductModalVisiblity] = useState(false);
  const [departmentModalVisibility, setDepartmentModalVisibility] = useState(false);
  const [customerTypeModalVisibility, setCustomerTypeModalVisibility] = useState(false);

  const setStateCallback = <T,>(id: string, data: T) => {
    setState(prev => ({ ...prev, [id]: data }));
  };

  const setApplication = <T extends Record<string, any>>(updated: T, id: string) => {
    setState(prev => ({ ...prev, [id]: updated }));
  };

  const [state, setState] = useState<IDiscountState>({
    title: "",
    application: null,
    value_type: null,
    value: 0,
    custom: false,
    tracked: false,
    departments: [],
    products: [],
    discounted_customer_types: [],
    apply_discount_ranges: false,
  });

  const [timeState, setTimeState] = useState<ITimeState>({
    start_date: today,
    end_date: today,
    daily_start_time: null,
    daily_end_time: null,
    interval: "weeks",
    auto_apply: false,
    ends_type: "never",
    schedule_type: "date_range",
    repeat_amount: 1,
    weekdays: [],
  });

  function handleDiscountProductModal() {
    setDiscountProductModalVisiblity(!discountProductModalVisibility);
  }

  function handleDepartmentModal() {
    setDepartmentModalVisibility(!departmentModalVisibility);
  }

  function handleCustomerTypeModal() {
    setCustomerTypeModalVisibility(!customerTypeModalVisibility);
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setState({ ...state, [id]: value });
  }

  function handleDropdownChange(value: string) {
    setState({ ...state, value_type: value });
  }

  function handleToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const id = event?.target?.id;
    const checked = event?.target?.checked;

    if (id === undefined || checked === undefined) {
      return;
    }

    if (id === "auto_apply") {
      setTimeState({ ...timeState, auto_apply: checked });
    } else {
      setState({ ...state, [id]: checked });
    }
  }

  function handleClear(id: string) {
    setState(prev => ({ ...prev, [id]: [] }));
  }

  function updateTimeState(key: string, value: any) {
    setTimeState(prev => ({ ...prev, [key]: value }));
  }

  async function createDiscount() {
    const startDateBuffer = state?.apply_discount_ranges ? formatDate(timeState?.start_date) : null;
    const endDateBuffer = state?.apply_discount_ranges ? formatDate(timeState?.end_date) : null;
    const discountRes = await PostDiscount(
      {
        title: state.title,
        value_type: state.value_type,
        application: "line_item",
        value: state.value,
        custom: state.custom,
        tracked: state.tracked,
        auto_apply: timeState?.auto_apply,
        start_date: startDateBuffer,
        end_date: endDateBuffer,
        daily_start_time: timeState?.daily_start_time ?? null,
        daily_end_time: timeState?.daily_end_time ?? null,
        // Enhancements
        repeat_amount: Number(timeState?.repeat_amount),
        repeat_interval: timeState?.interval,
        week_days: timeState?.weekdays,
        schedule_type: timeState?.schedule_type,
        product_ids: state?.products.map(product => {
          return {
            product_id: product.id,
            application: product.pivot?.application,
          };
        }),
        department_ids: state?.departments.map(dept => {
          return {
            department_id: dept.id,
            application: dept.pivot.application,
          };
        }),
        customer_type_ids: state?.discounted_customer_types?.map(type => {
          return type.id;
        }),
      },
      true,
    );

    if (discountRes.status !== StatusCode.OK) {
      dispatch(showError(t("Can't Create Discount")));
      return;
    }

    dispatch(showSuccess(t("Discount Created Successfully")));
    history.push("/admin/settings/discount");
  }

  const primaryAction = {
    content: t("Create"),
    action: createDiscount,
  };

  const secondaryActions: IMultipleActionDropdownPageAction = {
    label: t("Options"),
    dropdownProps: {
      alignment: "right",
      options: [
        {
          type: "handler",
          label: t("Discount Product"),
          handler: handleDiscountProductModal,
          icon: "percentage",
        },
        {
          type: "handler",
          label: t("Discount Departments"),
          handler: handleDepartmentModal,
          icon: "tag",
        },
        {
          type: "handler",
          label: t("Discount Customer Types"),
          handler: handleCustomerTypeModal,
          icon: "person",
        },
      ],
    },
  };

  return (
    <Page
      title={t("New Discount")}
      narrow
      primaryAction={primaryAction}
      multipleActionDropdownAction={secondaryActions}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Discounts"),
          url: "/admin/settings/discount",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={state.title}
                label={t("Title")}
                id="title"
                onChange={handleInputChange}
                placeholder={t("Title")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label={t("Value Type")}
                className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200"
                placeholder={t("Value Type")}
                defaultValue={"percent"}
                onChange={(value: string) => handleDropdownChange(value)}
              >
                <Option value={"percent"}>{t("Percent")}</Option>
                <Option value={"fixed_amount"}>{t("Fixed Amount")}</Option>
                <Option value={"cost_plus_percent"}>{t("Cost + Percent")}</Option>
              </Select>
              <Input
                value={state.value}
                label={t("Value")}
                id="value"
                onChange={handleInputChange}
                placeholder={t("Value")}
                type="number"
                prefix={state.value_type === "percent" || state.value_type === "cost_plus_percent" ? "%" : "$"}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Toggle
                labelRight={t("Customizable")}
                id="custom"
                onChange={handleToggleChange}
                checked={state.custom}
                size="medium"
              />

              <Toggle
                id="auto_apply"
                labelRight={t("Automatic Discount")}
                onChange={handleToggleChange}
                checked={timeState?.auto_apply}
                size="medium"
              />

              <Toggle
                id="apply_discount_ranges"
                labelRight={t("Discount Dates")}
                onChange={handleToggleChange}
                checked={state?.apply_discount_ranges}
                size="medium"
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Sheet
        open={discountProductModalVisibility}
        title={t("Discount Products")}
        okText={t("Apply")}
        onOk={handleDiscountProductModal}
        cancelText={t("Close")}
        okDisabled={state?.products?.some(
          product => product.pivot?.application === undefined || product.pivot?.application === null,
        )}
        onCancel={handleDiscountProductModal}
        secondaryText={t("Clear")}
        onSecondary={() => handleClear("products")}
        secondaryDisabled={state?.products.length === 0}
        backDropCancel={false}
        size="medium"
        closable
      >
        <DiscountProductSelect
          stateCallback={setStateCallback}
          onApplicationSelect={setApplication}
          products={state.products}
        />
      </Sheet>

      <Sheet
        open={departmentModalVisibility}
        title={t("Discount Departments")}
        okText={t("Apply")}
        onOk={handleDepartmentModal}
        okDisabled={state?.departments?.some(department => department.pivot?.application === undefined)}
        cancelText={t("Close")}
        onCancel={handleDepartmentModal}
        secondaryText={t("Clear")}
        onSecondary={() => handleClear("departments")}
        secondaryDisabled={state?.departments.length === 0}
        backDropCancel={false}
        size="medium"
        closable
      >
        <DiscountDepartmentSelect
          stateCallback={setStateCallback}
          onApplicationSelect={setApplication}
          discountDepartmentState={state?.departments}
        />
      </Sheet>

      <Sheet
        open={customerTypeModalVisibility}
        title={t("Discount Customer Types")}
        okText={t("Apply")}
        onOk={handleCustomerTypeModal}
        cancelText={t("Close")}
        onCancel={handleCustomerTypeModal}
        secondaryText={t("Clear")}
        onSecondary={() => handleClear("discounted_customer_types")}
        secondaryDisabled={state?.discounted_customer_types.length === 0}
        backDropCancel={false}
        size="small"
        closable
        overflow
      >
        <DiscountCustomerTypeSelect
          stateCallback={setStateCallback}
          discountCustomerType={state?.discounted_customer_types}
        />
      </Sheet>

      <DiscountDateSelect
        updateState={updateTimeState}
        timeState={timeState}
        show_dates={state?.apply_discount_ranges}
      />
    </Page>
  );
}
